/**
 * Where "safety home" is, for the user currently looking at the screen.
 *
 * THE RULE THIS ENCODES
 * ---------------------
 * **Register when no field-specific variant exists; repoint when one does.**
 *
 * `AppRoutes` serves exactly ONE of several mutually exclusive <Routes> trees
 * per user, so a path is registered per-tree and a path a tree lacks resolves
 * to that tree's catch-all — a silent bounce, not a 404. There are two ways to
 * fix a link that bounces, and which one is right depends on the destination:
 *
 *   - `/safety/my-training` (June 12) had NO field variant. One screen, wanted
 *     by everybody, missing from two trees. Registering it in all three was
 *     correct and is what shipped.
 *
 *   - `/safety` DOES have a field variant. `/safety` renders `SafetyScreen`,
 *     `/field/safety` renders `FieldSafetyScreen`, and both are already
 *     registered — the office one in the AppLayout tree, the field one in both
 *     field trees. Registering `/safety` in the field trees would hand field
 *     workers the office screen as a second route to a thing they already have,
 *     which is why these call sites repoint instead.
 *
 * WHY THIS IS A FUNCTION AND NOT A CONSTANT
 * -----------------------------------------
 * The call sites are SHARED. `IncidentReportForm` and `MyTrainingScreen` are
 * each rendered in all three trees, so there is no single correct literal:
 * hardcoding `/field/safety` fixes the field worker and breaks the office user
 * in precisely the same way, sending them to a path their tree does not
 * register. The destination is a function of the persona, so it is expressed
 * as one.
 *
 * Pass `isFieldWorker` straight from `useAuth()`.
 */
export function safetyHomePath(isFieldWorker: boolean): string {
  return isFieldWorker ? '/field/safety' : '/safety';
}

/**
 * Safety home with the near-miss report requested on arrival.
 *
 * `SafetyScreen` consumes `?report=near-miss`, opens its near-miss modal and
 * strips the param so a refresh does not reopen it. The signpost on the
 * incident form exists to land the reader ON the open form rather than on a
 * screen where they have to find it again, so the param carries the whole
 * point of the link.
 *
 * CAVEAT, DELIBERATE: `FieldSafetyScreen` does NOT read this param today. It
 * has the same `showNearMissModal` state and renders the same modal; it simply
 * never wired the query string. A field worker following the signpost
 * therefore lands on the right screen with the modal closed. The param is kept
 * rather than dropped so the URL stays honest about what was asked for, and so
 * wiring it later is a change in one screen rather than a hunt for call sites.
 */
export function nearMissReportPath(isFieldWorker: boolean): string {
  return `${safetyHomePath(isFieldWorker)}?report=near-miss`;
}
